import React, { useState } from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/custom/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import PopupConfirm from '@/components/custom/popup-confirm'
import { Plus, Trash2, ChevronDown, PencilLine } from 'lucide-react'
import DialogGroupTag from './dialog'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import useDebounce from '@/hooks/use-debounce'
import { toast } from 'sonner'
import { getTags, deleteGroupTag } from '@/api/settings'

import QUERY_KEY from '@/constants/use-query-key.js'
import { throwException } from '@/exceptions/throw-exception'

export function GroupTags() {
  const queryClient = useQueryClient()
  const [searchText, setSearchText] = useState('')
  const [openAccordionItems, setOpenAccordionItems] = useState([])
  const debounceSearchText = useDebounce(searchText, 500)
  const [isOpenDialogGroupTag, setIsOpenDialogGroupTag] = useState(false)
  const [editGroupTag, setEditGroupTag] = useState(null)

  const { isLoading, data } = useQuery({
    queryKey: [QUERY_KEY.GET_LIST_TAGS, { search: debounceSearchText.trim() }],
    queryFn: async () => {
      let params = null
      if (searchText) {
        params = {
          tag_name: searchText.trim(),
        }
      }

      const { data } = await getTags(params)

      if (searchText.trim() !== '') {
        setOpenAccordionItems(data.map((group) => group._id))
      } else {
        setOpenAccordionItems([])
      }

      return data
    },
  })

  const { mutate: handleDeleteGroupTag } = useMutation({
    mutationFn: (groupTagId) => deleteGroupTag(groupTagId),
    onSuccess: () => {
      toast.success('Xóa nhóm tags thành công')

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_LIST_TAGS],
      })
    },
    onError: throwException,
  })

  return (
    <React.Fragment>
      <div className="flex h-full w-full flex-col overflow-hidden">
        <Card className="h-full overflow-hidden">
          <CardContent className="flex h-full flex-col gap-2 p-4">
            <div className="sticky top-0 flex items-center justify-between gap-2">
              <h1 className="text-lg font-bold">Tags </h1>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="Tìm kiếm theo tên tags"
                  value={searchText}
                  className="w-[250px]"
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Button
                  leftSection={<Plus className="h-4 w-4" />}
                  onClick={() => {
                    setIsOpenDialogGroupTag(true)
                    setEditGroupTag(null)
                  }}
                >
                  Thêm nhóm tags
                </Button>
              </div>
            </div>
            <div className={`flex-1 overflow-auto`}>
              {!isLoading && !data?.length ? (
                <div className="flex h-full w-full items-center justify-center">
                  <p>Không có dữ liệu.</p>
                </div>
              ) : (
                <Accordion
                  className="w-full"
                  type="multiple"
                  collapsible="true"
                  value={openAccordionItems}
                  onValueChange={setOpenAccordionItems}
                >
                  {(data ?? [])?.map((groupTag) => (
                    <AccordionItem
                      key={groupTag._id}
                      value={groupTag._id}
                      className="border-0"
                    >
                      <AccordionTrigger
                        className="flex w-full border-b border-[#E2E8F0]"
                        hideIcon
                      >
                        {groupTag.name}
                        <div className="flex items-center gap-2">
                          <PencilLine
                            className="h-4 w-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditGroupTag(groupTag)
                              setIsOpenDialogGroupTag(true)
                            }}
                          />
                          <PopupConfirm
                            trigger={
                              <Trash2 className="h-4 w-4 cursor-pointer text-[hsl(var(--icon-danger))]" />
                            }
                            title={'Xóa nhóm tags'}
                            description={`Bạn có chắc chắn muốn xóa nhóm tags ${groupTag.name} không?`}
                            onConfirm={() => handleDeleteGroupTag(groupTag._id)}
                          />
                          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {groupTag.tags?.map((tag, index) => (
                          <div
                            key={index}
                            className="w-full border-b border-[#E2E8F0] bg-[#F8FAFC] p-2"
                          >
                            {tag}
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      {isOpenDialogGroupTag && (
        <DialogGroupTag
          open={isOpenDialogGroupTag}
          editGroupTag={editGroupTag}
          handleClose={() => {
            setEditGroupTag(editGroupTag)
            setIsOpenDialogGroupTag(false)
          }}
        />
      )}
    </React.Fragment>
  )
}
